// Глобальный словарь терминов для сквозной подсветки (LEARNING-SYSTEM §8.2).
//
// Источник — локальная SQLite через PowerSync: словарь доступен офлайн и
// оживает сам при синке. Настройки пользователя (`users.glossary_highlight_*`)
// читаются оттуда же и РЕАЛЬНО влияют на слой (Wire-up = DoD).
//
// Индекс мемоизируется по СТАБИЛЬНОМУ ключу (кол-во терминов + max(updated_at)
// + настройки + скоуп), а НЕ по массиву: `usePowerSyncQuery` отдаёт новый
// массив на каждый синк, и мемоизация по ссылке пересобирала бы регулярку
// постоянно (известная проблема TipTap-расширения).

import { createContext, createElement, type ReactNode, useContext, useMemo } from "react";
import type { GlossaryTermAttrs } from "@/components/notes/editor/extensions/glossary-tooltip-mark";
import { useSession } from "@/hooks/use-session";
import {
	buildGlossaryIndex,
	EMPTY_GLOSSARY_INDEX,
	type GlossaryIndex,
	type GlossaryTermLite,
} from "@/lib/glossary/build-index";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

/** Скоуп подсветки: тема и/или курс, в контексте которых показан текст. */
export interface GlossaryScope {
	planId?: string;
	subsectionId?: string;
}

interface LocalTermRow {
	id: string;
	term: string;
	definition: string;
	importance: string | null;
	status: string | null;
	material_id: string | null;
	subsection_id: string | null;
	citations: string | null;
	updated_at: string | null;
}

interface LocalSubsectionRow {
	id: string;
	plan_id: string | null;
}

interface LocalGlossarySettingsRow {
	glossary_highlight_enabled: number | null;
	glossary_highlight_course_only: number | null;
}

interface GlossaryDictionary {
	enabled: boolean;
	courseOnly: boolean;
	terms: GlossaryTermLite[];
	/** Стабильный ключ словаря: кол-во + max(updated_at). */
	version: string;
	/** subsectionId → planId (нужен, чтобы понять «термин этого курса»). */
	planBySubsection: Map<string, string>;
	/** Скомпилированный индекс под скоуп; кэширован на весь словарь. */
	getIndex: (scopeKey: string, scope: GlossaryScope) => GlossaryIndex | null;
}

const EMPTY_DICTIONARY: GlossaryDictionary = {
	enabled: false,
	courseOnly: false,
	terms: [],
	version: "0:",
	planBySubsection: new Map(),
	getIndex: () => null,
};

const GlossaryDictionaryContext = createContext<GlossaryDictionary>(EMPTY_DICTIONARY);

/** Приоритет определения (§8.3): manual > verified > edited > ai-generated. */
const STATUS_RANK: Record<string, number> = {
	manual: 0,
	verified: 1,
	edited: 2,
	"ai-generated": 3,
};

function parseCitation(raw: string | null): GlossaryTermLite["citation"] {
	if (!raw) return null;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed) || parsed.length === 0) return null;
		const first = parsed[0] as { page?: number; timestamp?: number; snippet?: string };
		return {
			page: typeof first.page === "number" ? first.page : undefined,
			timestamp: typeof first.timestamp === "number" ? first.timestamp : undefined,
			snippet: typeof first.snippet === "string" ? first.snippet : undefined,
		};
	} catch {
		return null;
	}
}

function toImportance(raw: string | null): GlossaryTermLite["importance"] {
	return raw === "high" || raw === "medium" || raw === "low" ? raw : null;
}

/**
 * Провайдер словаря. Смонтирован ОДИН раз в `__root.tsx`: иначе каждый
 * markdown-рендер (сообщение репетитора, блок конспекта) заводил бы свой
 * PowerSync-watcher.
 */
export function GlossaryDictionaryProvider({
	children,
}: {
	children: ReactNode;
}): React.ReactElement {
	const { user } = useSession();
	const userId = user?.id ?? "";

	const settingsQuery = usePowerSyncQuery<LocalGlossarySettingsRow>(
		`SELECT glossary_highlight_enabled, glossary_highlight_course_only FROM users WHERE id = ? LIMIT 1`,
		[userId],
	);
	const termsQuery = usePowerSyncQuery<LocalTermRow>(
		`SELECT id, term, definition, importance, status, material_id, subsection_id, citations, updated_at
		   FROM glossary_terms
		  WHERE user_id = ? AND deleted_at IS NULL`,
		[userId],
	);
	const subsectionsQuery = usePowerSyncQuery<LocalSubsectionRow>(
		`SELECT id, plan_id FROM study_subsections`,
		[],
	);

	const settingsRow = settingsQuery.data?.[0];
	// Колонка ещё не приехала (null) → серверный default = включено.
	const enabled = settingsRow?.glossary_highlight_enabled !== 0;
	const courseOnly = settingsRow?.glossary_highlight_course_only === 1;

	const rows = termsQuery.data;
	const subsectionRows = subsectionsQuery.data;

	const version = useMemo(() => {
		let max = "";
		for (const row of rows ?? []) {
			if (row.updated_at && row.updated_at > max) max = row.updated_at;
		}
		return `${rows?.length ?? 0}:${max}`;
	}, [rows]);

	const subsectionVersion = subsectionRows?.length ?? 0;

	// biome-ignore lint/correctness/useExhaustiveDependencies: пересборка по СТАБИЛЬНОМУ ключу (version), а не по ссылке массива — иначе индекс пересобирается на каждый синк
	const terms = useMemo<GlossaryTermLite[]>(() => {
		const mapped = (rows ?? []).map<GlossaryTermLite>((row) => ({
			id: row.id,
			term: row.term,
			definition: row.definition,
			importance: toImportance(row.importance),
			status: row.status,
			materialId: row.material_id,
			subsectionId: row.subsection_id,
			citation: parseCitation(row.citations),
		}));
		// Порядок = приоритет: при одинаковом термине выигрывает первый (см.
		// buildGlossaryIndex). Свежесть — вторым ключом.
		return mapped.sort((a, b) => {
			const rank = (STATUS_RANK[a.status ?? ""] ?? 9) - (STATUS_RANK[b.status ?? ""] ?? 9);
			return rank !== 0 ? rank : a.term.localeCompare(b.term);
		});
	}, [version]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: карта пересобирается по кол-ву тем, не по ссылке массива
	const planBySubsection = useMemo(() => {
		const map = new Map<string, string>();
		for (const row of subsectionRows ?? []) {
			if (row.plan_id) map.set(row.id, row.plan_id);
		}
		return map;
	}, [subsectionVersion]);

	// Компиляция индекса — ОДИН раз на (словарь × скоуп), а не на каждый
	// RichContent: у документа лекции сотня блоков, и сотня регулярок по 800
	// альтернатив съела бы кадр.
	const getIndex = useMemo(() => {
		const cache = new Map<string, GlossaryIndex | null>();
		return (scopeKey: string, scope: GlossaryScope): GlossaryIndex | null => {
			const cached = cache.get(scopeKey);
			if (cached !== undefined) return cached;
			let built: GlossaryIndex | null = null;
			if (enabled && terms.length > 0) {
				const hasScope = Boolean(scope.planId || scope.subsectionId);
				// `courseOnly` (тумблер «Только термины текущего курса») сужает слой
				// ТОЛЬКО там, где скоуп реально известен. Без скоупа он схлопывал
				// словарь в пустой — тумблер «сузить» выключал подсветку целиком на
				// всех поверхностях (rules/logging.md §13c: тихая деградация).
				const narrow = courseOnly && hasScope;
				const filtered = terms.filter((term) => {
					if (hasScope && isInScope(term, scope, planBySubsection)) return true;
					if (narrow) return false;
					return term.importance === "high";
				});
				built = filtered.length === 0 ? EMPTY_GLOSSARY_INDEX : buildGlossaryIndex(filtered);
			}
			cache.set(scopeKey, built);
			return built;
		};
	}, [enabled, courseOnly, terms, planBySubsection]);

	const value = useMemo<GlossaryDictionary>(
		() => ({ enabled, courseOnly, terms, version, planBySubsection, getIndex }),
		[enabled, courseOnly, terms, version, planBySubsection, getIndex],
	);

	return createElement(GlossaryDictionaryContext.Provider, { value }, children);
}

/** Термин относится к текущему курсу/теме? */
function isInScope(
	term: GlossaryTermLite,
	scope: GlossaryScope,
	planBySubsection: Map<string, string>,
): boolean {
	if (!term.subsectionId) return false;
	if (scope.subsectionId && term.subsectionId === scope.subsectionId) return true;
	if (scope.planId) return planBySubsection.get(term.subsectionId) === scope.planId;
	return false;
}

/**
 * Индекс для конкретной поверхности. Вне текущего курса подсвечиваем только
 * `importance: 'high'`, внутри — все термины темы/курса (§8.4).
 *
 * Возвращает `null`, когда слой выключен пользователем — потребитель просто
 * рендерит текст как раньше.
 */
export function useGlossaryIndex(scope?: GlossaryScope): GlossaryIndex | null {
	const dict = useContext(GlossaryDictionaryContext);
	const planId = scope?.planId;
	const subsectionId = scope?.subsectionId;
	const scopeKey = `${planId ?? ""}|${subsectionId ?? ""}`;

	return useMemo(
		() => dict.getIndex(scopeKey, { planId, subsectionId }),
		[dict.getIndex, scopeKey, planId, subsectionId],
	);
}

/** Найти термин по подписи метки — для делегированного поповера. */
export function useGlossaryTermLookup(): (termOrId: {
	term?: string | null;
	id?: string | null;
}) => GlossaryTermLite | null {
	const dict = useContext(GlossaryDictionaryContext);
	// biome-ignore lint/correctness/useExhaustiveDependencies: ключ — стабильная version словаря
	return useMemo(() => {
		const byId = new Map<string, GlossaryTermLite>();
		const byLower = new Map<string, GlossaryTermLite>();
		for (const term of dict.terms) {
			byId.set(term.id, term);
			const key = term.term.toLowerCase();
			if (!byLower.has(key)) byLower.set(key, term);
		}
		return ({ term, id }) => {
			if (id) {
				const byIdMatch = byId.get(id);
				if (byIdMatch) return byIdMatch;
			}
			if (term) return byLower.get(term.toLowerCase()) ?? null;
			return null;
		};
	}, [dict.version]);
}

/**
 * Термины словаря в форме, которую ждёт TipTap-марк подсветки
 * (`GlossaryTooltipMark`) — для поверхностей на редакторе, а не на RichContent:
 * чтение конспекта темы (§8.1 — ПЕРВАЯ поверхность слоя) и notes-editor.
 *
 * Фильтрация 1-в-1 с `useGlossaryIndex`: внутри скоупа — все термины темы/курса,
 * вне — только `importance: 'high'`; при выключенном слое — пустой массив (марк
 * получает пустой список и не подсвечивает ничего).
 */
export function useGlossaryTermsForScope(scope?: GlossaryScope): GlossaryTermAttrs[] {
	const dict = useContext(GlossaryDictionaryContext);
	const planId = scope?.planId;
	const subsectionId = scope?.subsectionId;
	const scopeKey = `${planId ?? ""}|${subsectionId ?? ""}`;

	// biome-ignore lint/correctness/useExhaustiveDependencies: ключ — стабильная version словаря + скоуп
	return useMemo<GlossaryTermAttrs[]>(() => {
		const index = dict.getIndex(scopeKey, { planId, subsectionId });
		if (!index) return [];
		return [...index.byLower.values()].map((term) => ({
			id: term.id,
			term: term.term,
			definition: term.definition,
		}));
	}, [dict.version, dict.getIndex, scopeKey, planId, subsectionId]);
}
